export type ResetSource = "field" | "frames" | "random";
export type FitMode = "cover" | "contain";
export type ParticlePoint = readonly [number, number];

export type PartialFrame = {
  id: string;
  particles: readonly ParticlePoint[];
};

export type ParticleFrame = PartialFrame & {
  particleCount: number;
};

export type PresetParamFields = {
  fieldUrl?: string;
  fieldFit?: FitMode;
  viewFit?: FitMode;
  resetSource?: ResetSource;
  particleCount: number;
  worldMode?: "viewport";
  worldWidth: number;
  worldHeight: number;
  dotSize: number;
  pointerRadius: number;
  pointerRadiusDown: number;
  pointerForce: number;
  pointerForceDown: number;
  returnForce: number;
  returnDamping: number;
  dt: number;
  mu_k: number;
  sigma_k: number;
  mu_g: number;
  sigma_g: number;
  c_rep: number;
  r_rep: number;
  resetAttempts: number;
  initialFieldScattering: number;
};

export type RuntimeParams = PresetParamFields & {
  viewWidth: number;
  viewHeight: number;
  gridSizeX: number;
  gridSizeY: number;
  dotSizeX: number;
  dotSizeY: number;
  binSize: number;
  binCount: number;
  prefixSumIterations: number;
  w_k: number;
};

export type PartialParams = Partial<PresetParamFields>;

export type PartialBreakpoint = PartialParams & {
  minWidth: number;
};

export type PartialPreset = {
  defaults: PartialParams;
  breakpoints?: readonly PartialBreakpoint[];
  frames?: readonly PartialFrame[];
  resetSource?: ResetSource;
};

export type Preset = Omit<PartialPreset, "frames"> & {
  frames?: readonly ParticleFrame[];
};

export type SimulationDebugParams = Pick<
  RuntimeParams,
  | "dt"
  | "mu_k"
  | "sigma_k"
  | "mu_g"
  | "sigma_g"
  | "c_rep"
  | "r_rep"
  | "returnForce"
  | "returnDamping"
>;

export type Simulation = {
  params: RuntimeParams;
  start(): Promise<boolean>;
  stop(): void;
  reset(): void;
  pause(): void;
  resume(): void;
};

const REQUIRED_PARAM_KEYS = [
  "dotSize",
  "pointerRadius",
  "pointerRadiusDown",
  "pointerForce",
  "pointerForceDown",
  "returnForce",
  "returnDamping",
  "dt",
  "mu_k",
  "sigma_k",
  "mu_g",
  "sigma_g",
  "c_rep",
  "r_rep",
  "resetAttempts",
  "initialFieldScattering",
] as const;

export function definePresets(
  presets: Readonly<Record<string, PartialPreset>>,
): Readonly<Record<string, Preset>> {
  const normalized = {} as Record<string, Preset>;

  for (const name in presets) {
    const preset = presets[name];
    if (!preset) continue;
    normalized[name] = normalizePreset(name, preset);
  }

  return normalized;
}

export function resolvePresetParams(preset: Preset, width: number): PartialParams {
  const params = { ...preset.defaults };

  for (const breakpoint of sortedBreakpoints(preset.breakpoints ?? [])) {
    if (width >= breakpoint.minWidth) {
      Object.assign(params, breakpoint);
    }
  }

  return params;
}

function normalizePreset(name: string, preset: PartialPreset): Preset {
  const frames = preset.frames?.map((frame) => ({
    ...frame,
    particleCount: frame.particles.length,
  }));

  const frameParticleCount = frames?.reduce((count, frame) => count + frame.particleCount, 0);
  const defaults = {
    ...preset.defaults,
    ...(frameParticleCount == null ? {} : { particleCount: frameParticleCount }),
  };

  const normalized: Preset = {
    defaults,
    ...(preset.breakpoints ? { breakpoints: preset.breakpoints } : {}),
    ...(preset.resetSource ? { resetSource: preset.resetSource } : {}),
    ...(frames ? { frames } : {}),
  };

  assertPresetComplete(name, normalized);
  return normalized;
}

function assertPresetComplete(name: string, preset: Preset): void {
  const hasFrames = (preset.frames?.length ?? 0) > 0;
  const breakpoints = preset.breakpoints?.length ? preset.breakpoints : undefined;

  if (!breakpoints) {
    assertParamsComplete(name, "defaults", preset.defaults, hasFrames);
    return;
  }

  if (missingParams(preset.defaults, hasFrames).length > 0 && !breakpoints.some((breakpoint) => breakpoint.minWidth === 0)) {
    throw new Error(`Incomplete particle preset "${name}": breakpoint-completed presets need a minWidth 0 fallback`);
  }

  for (const breakpoint of sortedBreakpoints(breakpoints)) {
    assertParamsComplete(
      name,
      `breakpoint ${breakpoint.minWidth}`,
      resolvePresetParams(preset, breakpoint.minWidth),
      hasFrames,
    );
  }
}

function sortedBreakpoints(breakpoints: readonly PartialBreakpoint[]): PartialBreakpoint[] {
  return [...breakpoints].sort((a, b) => a.minWidth - b.minWidth);
}

function assertParamsComplete(
  presetName: string,
  location: string,
  params: PartialParams,
  hasFrames: boolean,
): void {
  const missing = missingParams(params, hasFrames);

  if (missing.length > 0) {
    throw new Error(`Incomplete particle preset "${presetName}" ${location}: ${missing.join(", ")}`);
  }
}

function missingParams(params: PartialParams, hasFrames: boolean): string[] {
  const missing: string[] = [];

  for (const key of REQUIRED_PARAM_KEYS) {
    if (typeof params[key] !== "number") missing.push(key);
  }

  const hasViewportWorld = params.worldMode === "viewport";
  const hasFixedWorld = typeof params.worldWidth === "number" && typeof params.worldHeight === "number";

  if (!hasViewportWorld && !hasFixedWorld) {
    missing.push("worldMode: viewport or worldWidth + worldHeight");
  }

  if (!hasFrames && typeof params.particleCount !== "number") {
    missing.push("particleCount");
  }

  return missing;
}
